import { memo } from 'react'
import { useTranslation } from 'react-i18next'
import { PanelLeftClose, PanelLeft, FolderPlus, Upload } from 'lucide-react'
import SelectionToolbar from './Toolbar/SelectionToolbar'
import SortControls from './Toolbar/SortControls'
import ViewToggle from './Toolbar/ViewToggle'
import { AdvancedFilterBar } from './Toolbar/AdvancedFilterBar'
import { AppliedFilters, FilterField } from './Toolbar/filterTypes'
import { Pagination as PaginationData } from '@features/file-explorer/types'

interface FileExplorerToolbarProps {
  selectedCount: number
  totalCount: number
  pagination: PaginationData
  sortBy: string
  sortDirection: 'asc' | 'desc'
  filters: AppliedFilters
  filterFields: FilterField[]
  viewMode: 'table' | 'grid'
  loadingElement: string | null
  isSidebarOpen: boolean
  onToggleSidebar: () => void
  onClearSelection: () => void
  onSelectAll: () => void
  onBulkDownload: () => void
  onBulkDelete: () => void
  onSortChange: (sortBy: string) => void
  onDirectionToggle: () => void
  onApplyFilters: (filters: AppliedFilters) => void
  onViewChange: (view: 'table' | 'grid') => void
  onCreateFolder: () => void
  onUploadDocument: () => void
}

function FileExplorerToolbar({
  selectedCount,
  totalCount,
  pagination,
  sortBy,
  sortDirection,
  filters,
  filterFields,
  viewMode,
  loadingElement,
  isSidebarOpen,
  onToggleSidebar,
  onClearSelection,
  onSelectAll,
  onBulkDownload,
  onBulkDelete,
  onSortChange,
  onDirectionToggle,
  onApplyFilters,
  onViewChange,
  onCreateFolder,
  onUploadDocument
}: FileExplorerToolbarProps) {
  const { t } = useTranslation()

  return (
    <div className="bg-base-100 p-2 sm:p-4">
      {/* Desktop: New Layout */}
      <div className="hidden md:flex flex-col gap-3">
        {/* Row 1: Sidebar button | Element count | Selection bar | | Create folder/document buttons | */}
        <div className="flex items-center gap-3">
          {/* Left: Sidebar Toggle */}
          <button
            onClick={onToggleSidebar}
            className="btn btn-sm btn-ghost"
            title={isSidebarOpen ? t('fileExplorer.folderTree.hideFolderTree') : t('fileExplorer.folderTree.showFolderTree')}
          >
            {isSidebarOpen ? (
              <PanelLeftClose className="w-4 h-4" />
            ) : (
              <PanelLeft className="w-4 h-4" />
            )}
          </button>

          {/* Element Count */}
          <div className="text-sm font-medium text-base-content/70 whitespace-nowrap">
            {t('fileExplorer.elementsCount', {
              from: pagination.from,
              to: pagination.to,
              count: pagination.count
            })}
          </div>

          {/* Selection Bar - Takes remaining space */}
          <div className="flex-none">
            <SelectionToolbar
              selectedCount={selectedCount}
              totalCount={totalCount}
              onClearSelection={onClearSelection}
              onSelectAll={onSelectAll}
              onBulkDownload={onBulkDownload}
              onBulkDelete={onBulkDelete}
            />
          </div>
          <div className="flex-1"></div>

          {/* Right: Create Folder/Document Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onCreateFolder}
              className="btn btn-sm btn-soft btn-secondary gap-2"
              title={t('folders.create_button')}
            >
              <FolderPlus className="w-4 h-4" />
              {t('folders.create_button')}
            </button>
            <button
              onClick={onUploadDocument}
              className="btn btn-sm btn-primary gap-2"
              title={t('documents.upload_button')}
              data-testid="upload-document-button"
            >
              <Upload className="w-4 h-4" />
              {t('documents.upload_button')}
            </button>
          </div>
        </div>

        {/* Row 2: Filters | Sort buttons | View selector */}
        <div className="flex items-center gap-3">
          {/* Left: Filters - Takes most space */}
          <div className="flex-1 flex justify-start">
            <AdvancedFilterBar
              filterFields={filterFields}
              initialFilters={filters}
              onApplyFilters={onApplyFilters}
              isLoading={loadingElement === 'filter'}
            />
          </div>

          {/* Right: Sort Controls + View Toggle */}
          <div className="flex items-center gap-2">
            <SortControls
              sortBy={sortBy}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
              onDirectionToggle={onDirectionToggle}
              loadingSortField={loadingElement === 'sort-field'}
              loadingDirection={loadingElement === 'sort-direction'}
            />
            <ViewToggle view={viewMode} onViewChange={onViewChange} />
          </div>
        </div>
      </div>
    </div>
  )
}

// Custom comparison to prevent unnecessary re-renders
function arePropsEqual(prevProps: FileExplorerToolbarProps, nextProps: FileExplorerToolbarProps) {
  return (
    prevProps.selectedCount === nextProps.selectedCount &&
    prevProps.totalCount === nextProps.totalCount &&
    prevProps.pagination === nextProps.pagination &&
    prevProps.sortBy === nextProps.sortBy &&
    prevProps.sortDirection === nextProps.sortDirection &&
    prevProps.filters === nextProps.filters &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.loadingElement === nextProps.loadingElement &&
    prevProps.isSidebarOpen === nextProps.isSidebarOpen
  )
}

export default memo(FileExplorerToolbar, arePropsEqual)
